import { map } from 'underscore';

// for uploading image during crud-ad workflow, there are additional
// async operations needed after the asset upload to MT is complete
// so the default boundary on the progress bar is set to 80% to account
// for the additional time needed to process the image for crud-ad
export const defaultUploadProgressBoundary = 0.8;

/**
 * Helper to update the state of the progress bar based on current upload progress
 * @param {function} setProgress - function to update progress of files being uploaded
 * @param {number} index - index of the image being uploading within the array of images
 * to be uploaded
 * @param {number} uploadProgressBoundary - custom upload progress boundary for use in cases
 * where the amount of processing happening after upload to MT is complete is different than
 * default case used by crud-ad workflow. 1 is 100% (show full progress bar when upload is done).
 * @returns {function} event handler function which will take an event with loaded/total properties
 * and update progress based on the loaded state
 */
export const updateProgress = ({ setProgress, index, uploadProgressBoundary }) => (evt) => {
  const boundary = uploadProgressBoundary || defaultUploadProgressBoundary;
  const progress = (evt.loaded / evt.total) * boundary;

  setProgress(index, progress);
};

/**
 * Helper to update the progress of files in the array of files to be uploaded. Returns
 * a function which can directly be passed into setState if maintaining previewImageArr
 * via class component state.
 * @param {number} index - index of the file being uploading within the array of files
 * to be uploaded
 * @param {number} progress - current progress of uploading.
 * @returns {function} takes array of files to be uploaded as argument, will update the
 * progress of the file specified by index in the upload preview array
 */
export const updateFilePreviewArrayUploadProgress = (index, progress) => ({ previewArr }) => {
  const newPreviewArr = map(previewArr, (previous, i) => {
    if (i === index) {
      return {
        ...previous,
        progress,
      };
    }
    return previous;
  });

  return { previewArr: newPreviewArr };
};

/**
 * Helper to generate an array of files to be uploaded with properties used by the
 * upload progress UI.
 * @param {array} selectedFiles - array containing file objects
 * @param {array} fileSources - array of file data encoded as string.
 * @param {array} parsedInfos - array of object containing parsed info of each file.
 * @returns {array} array of files to be uploaded with properties
 * url (file data encoded), progress (0 for now, will be updated as file starts uploading),
 * size, name
 */
export const generatePreviewArray = ({ files = [], fileSources, customizedOptions = [] }) => {
  const previewArr = map(fileSources, (source, index) => {
    const uploadInfo = {
      url: source,
      progress: 0,
      size: files[index] ? files[index].size : 0,
      name: files[index] ? files[index].name : '',
      ...(customizedOptions[index] || {}),
    };
    return uploadInfo;
  });

  return previewArr;
};
